import { isDefined, isEmpty, makeValuesPredicate } from '@sgme/fp';
import { DocumentError } from 'apiConnector/document/document';
import classnames from 'classnames';
import { DocumentationV3, DocumentItem } from 'epic/common-form/Documentation';
import { useBlotterPanelContext } from 'hooks/useBlotterPanelContext';
import { useDownloadBrochure } from 'hooks/useDownloadBrochure';
import { useDownloadDlpDocument } from 'hooks/useDownloadDlpDocument';
import { useDownloadMarketingMaterials } from 'hooks/useDownloadMarketingMaterials';
import { useDownloadPreconfirmation } from 'hooks/useDownloadPreconfirmation';
import type { BlotterAnalyticCategory } from 'models/analytics';
import { DocumentType } from 'models/documents/DocumentType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { FC, useCallback, useMemo } from 'react';

import { BlotterPanelDocument } from './BlotterPanelDocument';
import { useTermsheetDownload } from './hooks';

export interface BlotterPanelDocumentListNextProps {
  className?: string;
  documents: DocumentationV3;
  e2e?: string;
  blotterItem: IBlotterItem;
}

const needsProductId = makeValuesPredicate([
  'PreConfirmation',
  'MarketingMaterial',
  'TermSheet',
] satisfies DocumentType[]);

function getDocumentOfType(documents: DocumentationV3, type: DocumentType, blotterItem?: IBlotterItem): DocumentItem[] {
  const document = documents.docs.find(doc => doc.type === type);
  if (blotterItem?.blotterItemType === 'DlpProduct') {
    return document?.items ?? [];
  }
  if (!needsProductId(type) || isDefined(blotterItem?.productId)) {
    return document?.items ?? [];
  } else {
    return [];
  }
}

function toDocumentError(error: string | null | undefined): DocumentError | undefined {
  if (error) {
    return {
      type: 'END_USER_ERROR',
      errorMessages: [error],
    };
  }
  return undefined;
}

export const BlotterPanelDocumentListLegacy: FC<BlotterPanelDocumentListNextProps> = ({
  className,
  documents,
  e2e = 'blotter-panel-documents',
  blotterItem,
}) => {
  const { blotterType } = useBlotterPanelContext()!;
  const analyticsCategory: BlotterAnalyticCategory | undefined = useMemo(() => {
    switch (blotterType) {
      case 'quotes':
        return 'Blotter My Quotes';
      case 'trades':
        return 'Blotter My Trades';
      case 'products':
        return 'Blotter My Products';
      case 'dlpProducts':
        return 'Blotter My flow products';
      default:
        return undefined;
    }
  }, [blotterType]);

  const {
    isDownloading: isDownloadingPreConf,
    download: downloadPreconfirmation,
    error: downloadPreConfError,
  } = useDownloadPreconfirmation();
  const {
    isDownloading: isDownloadingMarketingMaterials,
    download: downloadMarketingMaterials,
    error: downloadMarketingMaterialsError,
  } = useDownloadMarketingMaterials();

  const preConfirmationsDocument = useMemo(
    () => getDocumentOfType(documents, 'PreConfirmation', blotterItem),
    [documents, blotterItem],
  );
  const termSheetsDocument = useMemo(
    () => getDocumentOfType(documents, 'TermSheet', blotterItem),
    [documents, blotterItem],
  );
  const finalTermDocument = useMemo(
    () => getDocumentOfType(documents, 'FinalTerms', blotterItem),
    [documents, blotterItem],
  );
  const kidDocument = useMemo(() => getDocumentOfType(documents, 'Kid'), [documents]);
  const pgDocument = useMemo(() => getDocumentOfType(documents, 'ProductGovernance'), [documents]);
  const brochureDocument = useMemo(() => getDocumentOfType(documents, 'Brochure'), [documents]);
  const marketingMaterialsDocument = useMemo(
    () => getDocumentOfType(documents, 'MarketingMaterial', blotterItem),
    [documents, blotterItem],
  );
  const hasDocuments =
    !isEmpty(termSheetsDocument) ||
    !isEmpty(kidDocument) ||
    !isEmpty(pgDocument) ||
    !isEmpty(preConfirmationsDocument) ||
    !isEmpty(marketingMaterialsDocument) ||
    !isEmpty(brochureDocument);

  const onPreConfDownload = useCallback(() => {
    downloadPreconfirmation(blotterItem.productId, blotterItem);
  }, [blotterItem, downloadPreconfirmation]);
  const onMarketingMaterialsDownload = useCallback(() => {
    downloadMarketingMaterials(blotterItem.productId, blotterItem);
  }, [blotterItem, downloadMarketingMaterials]);

  const {
    download: downloadBrochure,
    error: errorBrochure,
    isDownloading: isDownloadingBrochure,
  } = useDownloadBrochure(blotterItem.externalQuoteId);

  const { onTermsheetDownload, isTermsheetDownloading, error: termsheetError } = useTermsheetDownload(blotterItem);
  const {
    downloadDlpDocument: onKidDownload,
    isDownloading: isKidDownloading,
    error: kidError,
  } = useDownloadDlpDocument('downloadKid.error');

  const {
    downloadDlpDocument: onProductGovernanceDownload,
    isDownloading: isProductGovernanceDownloading,
    error: productGovernanceError,
  } = useDownloadDlpDocument('downloadProductGovernance.error');

  const {
    downloadDlpDocument: onFinalTermDownload,
    isDownloading: isFinalTermDownloading,
    error: finalTermError,
  } = useDownloadDlpDocument('downloadFinalTerms.error');

  return hasDocuments ? (
    <div className={classnames(' w-100', className)} data-e2e={e2e}>
      <div className="card-body p-0 flex-wrap mt-3">
        <div className="list-group list-group-flush">
          {!isEmpty(brochureDocument) && (
            <BlotterPanelDocument
              type="Brochure"
              showDocumentStatus
              e2e="blotter-panel-brochure"
              nameKey="blotterPanel.BrochureDownload"
              documents={brochureDocument}
              isDownloading={isDownloadingBrochure}
              onDownload={downloadBrochure}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download brochure"
              error={errorBrochure}
            />
          )}
          {!isEmpty(preConfirmationsDocument) && (
            <BlotterPanelDocument
              type="PreConfirmation"
              showDocumentStatus
              e2e="blotter-panel-preconfirmation"
              nameKey="blotterPanel.PreconfirmationDownload"
              documents={preConfirmationsDocument}
              isDownloading={isDownloadingPreConf}
              onDownload={onPreConfDownload}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download preconfirmation"
              error={downloadPreConfError}
            />
          )}
          {!isEmpty(termSheetsDocument) && (
            <BlotterPanelDocument
              type="TermSheet"
              showDocumentStatus
              e2e="blotter-panel-termsheet"
              nameKey="blotterPanel.TermsheetDownload"
              documents={termSheetsDocument}
              isDownloading={isTermsheetDownloading}
              onDownload={onTermsheetDownload}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download termsheet"
              error={termsheetError}
            />
          )}

          {!isEmpty(kidDocument) && (
            <BlotterPanelDocument
              type="Kid"
              e2e="blotter-panel-kid"
              nameKey="blotterPanel.KidDocumentAccordionTitle"
              documents={kidDocument}
              isDownloading={isKidDownloading}
              onDownload={d => (d.public === false ? onKidDownload(d.documentToken!) : undefined)}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download KID language"
              error={toDocumentError(kidError)}
            />
          )}

          {!isEmpty(pgDocument) && (
            <BlotterPanelDocument
              type="ProductGovernance"
              e2e="blotter-panel-pg"
              nameKey="blotterPanel.PGDocumentAccordionTitle"
              documents={pgDocument}
              isDownloading={isProductGovernanceDownloading}
              onDownload={d => (d.public === false ? onProductGovernanceDownload(d.documentToken!) : undefined)}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download target market language"
              error={toDocumentError(productGovernanceError)}
            />
          )}

          {!isEmpty(marketingMaterialsDocument) && (
            <BlotterPanelDocument
              type="MarketingMaterial"
              showDocumentStatus
              e2e="blotter-panel-marketing-materials"
              nameKey="blotterPanel.MarketingMaterialsDownload"
              documents={marketingMaterialsDocument}
              isDownloading={isDownloadingMarketingMaterials}
              onDownload={onMarketingMaterialsDownload}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download marketing materials"
              error={downloadMarketingMaterialsError}
            />
          )}
          {!isEmpty(finalTermDocument) && (
            <BlotterPanelDocument
              type="FinalTerms"
              e2e="blotter-panel-ft-documents"
              nameKey="blotterPanel.FtDownload"
              documents={finalTermDocument}
              onDownload={d => (d.public === false ? onFinalTermDownload(d.documentToken!) : undefined)}
              isDownloading={isFinalTermDownloading}
              analyticsCategory={analyticsCategory}
              analyticsEvent="Download final term"
              error={toDocumentError(finalTermError)}
            />
          )}
        </div>
      </div>
    </div>
  ) : null;
};

BlotterPanelDocumentListLegacy.displayName = 'BlotterPanelDocumentListLegacy';
